import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Table, TableHead, TableRow, TableCell,
  TableBody, TableContainer, Paper, Button, Alert, IconButton,
  Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Chip, Tabs, Tab, TablePagination, FormControl,
  InputLabel, Select, MenuItem, Stack
} from '@mui/material';
import { Refresh as RefreshIcon, Delete as DeleteIcon } from '@mui/icons-material';
import equipmentService from '../../services/equipmentService';
import DepartmentSelector from '../DepartmentSelector';
import SkeletonBlock from '../ui/SkeletonBlock';
import { useToast } from '../ToastProvider';
import { usePermissions } from '../PermissionsContext';

export default function EquipmentApprovalsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [roleTab, setRoleTab] = useState('DM'); // 'DM' | 'BOARD'
  const [statusFilter, setStatusFilter] = useState('PendingDM');
  const [departmentId, setDepartmentId] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [rejectDlg, setRejectDlg] = useState({ open: false, id: null, comment: '' });
  const [deleteDlg, setDeleteDlg] = useState({ open: false, request: null });
  const toast = useToast();
  const { isSystemAdmin, roles } = usePermissions();
  const isBoardManager = roles?.includes('BoardManager');
  const isDepartmentManager = roles?.includes('DepartmentManager');

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      // SystemAdmin gets all, others get pending-approval
      let data;
      if (isSystemAdmin) {
        data = await equipmentService.getAll(statusFilter === 'ALL' ? null : statusFilter);
      } else {
        data = await equipmentService.getPendingApproval();
      }
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.response?.data || e.message || 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [statusFilter]);

  // When role tab changes, update default status filter
  useEffect(() => {
    setStatusFilter(roleTab === 'BOARD' ? 'PendingBoard' : 'PendingDM');
    setPage(0);
  }, [roleTab]);

  const handleApprove = async (request) => {
    try {
      if (request.status === 'PendingDM') {
        await equipmentService.approveDM(request.id, '');
      } else {
        await equipmentService.approveBoard(request.id, '');
      }
      toast.success('Request approved');
      loadData();
    } catch (e) {
      toast.error(e?.response?.data || e.message || 'Approval failed');
    }
  };

  const handleReject = async () => {
    if (!rejectDlg.id) return;
    try {
      await equipmentService.rejectRequest(rejectDlg.id, rejectDlg.comment || 'Rejected');
      toast.success('Request rejected');
      setRejectDlg({ open: false, id: null, comment: '' });
      loadData();
    } catch (e) {
      toast.error(e?.response?.data || e.message || 'Rejection failed');
    }
  };

  const handleDelete = async () => {
    if (!deleteDlg.request) return;
    try {
      await equipmentService.deleteRequest(deleteDlg.request.id);
      toast.success('Request deleted');
      setDeleteDlg({ open: false, request: null });
      loadData();
    } catch (e) {
      toast.error(e?.response?.data || e.message || 'Delete failed');
    }
  };

  // Filter by department on client side
  const listFiltered = useMemo(() => {
    let filtered = items;
    // Filter by tab scope
    if (!isSystemAdmin || statusFilter !== 'ALL') {
      if (roleTab === 'DM') {
        filtered = filtered.filter(r => ['PendingDM', 'Approved', 'Rejected', 'Cancelled'].includes(r.status));
      } else {
        filtered = filtered.filter(r => ['PendingBoard', 'Approved', 'Rejected', 'Ordered', 'Fulfilled', 'Cancelled'].includes(r.status));
      }
    }
    // Filter by department
    if (departmentId) {
      filtered = filtered.filter(r => r.departmentId === parseInt(departmentId));
    }
    return filtered;
  }, [items, roleTab, departmentId, statusFilter, isSystemAdmin]);

  const slice = listFiltered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const getStatusColor = (status) => {
    if (status === 'PendingDM' || status === 'PendingBoard') return 'warning';
    if (status === 'Approved' || status === 'Fulfilled') return 'success';
    if (status === 'Rejected' || status === 'Cancelled') return 'error';
    if (status === 'Ordered') return 'info';
    return 'default';
  };

  if (loading && items.length === 0) return <Box sx={{ p: 3 }}><SkeletonBlock lines={5} /></Box>;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header with filters */}
      <Box sx={{ mb: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 200px 300px' }, gap: 2, alignItems: 'center' }}>
        <Typography variant="h5">Equipment Approvals</Typography>
        <FormControl size="small">
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
            <MenuItem value="ALL">All</MenuItem>
            {roleTab === 'DM' && <MenuItem value="PendingDM">Pending (DM)</MenuItem>}
            {roleTab === 'BOARD' && <MenuItem value="PendingBoard">Pending (Board)</MenuItem>}
            <MenuItem value="Approved">Approved</MenuItem>
            <MenuItem value="Rejected">Rejected</MenuItem>
            <MenuItem value="Ordered">Ordered</MenuItem>
            <MenuItem value="Fulfilled">Fulfilled</MenuItem>
            <MenuItem value="Cancelled">Cancelled</MenuItem>
          </Select>
        </FormControl>
        <DepartmentSelector
          value={departmentId}
          onChange={(v) => setDepartmentId(v || '')}
          label="Department (All)"
          size="small"
        />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 2 }}>
        {/* Role tabs - show if user has both DM and Board roles */}
        {(isBoardManager || isSystemAdmin) && (isDepartmentManager || isSystemAdmin) && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={roleTab} onChange={(e, v) => setRoleTab(v)}>
              <Tab value="DM" label="Department Manager" />
              <Tab value="BOARD" label="Board" />
            </Tabs>
          </Box>
        )}

        <TableContainer>
          <Table size="small" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Employee</TableCell>
                <TableCell>Department</TableCell>
                <TableCell>Item</TableCell>
                <TableCell align="center">Qty</TableCell>
                <TableCell align="right">Est. Price</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {listFiltered.length === 0 && (
                <TableRow><TableCell colSpan={8} align="center">No items</TableCell></TableRow>
              )}
              {slice.map((req) => (
                <TableRow key={req.id} hover>
                  <TableCell>{new Date(req.requestDate).toLocaleDateString()}</TableCell>
                  <TableCell><strong>{req.employeeName}</strong></TableCell>
                  <TableCell>{req.departmentName}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">{req.subcategoryName}</Typography>
                    <Typography variant="caption" color="text.secondary">{req.categoryName}</Typography>
                  </TableCell>
                  <TableCell align="center">{req.quantity}</TableCell>
                  <TableCell align="right">{req.estimatedPrice ? `${req.estimatedPrice.toFixed(0)} лв.` : '-'}</TableCell>
                  <TableCell>
                    <Chip label={req.status} color={getStatusColor(req.status)} size="small" />
                  </TableCell>
                  <TableCell>
                    {/* DM can approve PendingDM */}
                    {(isDepartmentManager || isSystemAdmin) && req.status === 'PendingDM' && (
                      <>
                        <Button size="small" onClick={() => handleApprove(req)}>Approve (DM)</Button>
                        <Button size="small" color="error" onClick={() => setRejectDlg({ open: true, id: req.id, comment: '' })}>Reject</Button>
                      </>
                    )}
                    {/* Board can approve PendingBoard */}
                    {(isBoardManager || isSystemAdmin) && req.status === 'PendingBoard' && (
                      <>
                        <Button size="small" onClick={() => handleApprove(req)}>Approve</Button>
                        <Button size="small" color="error" onClick={() => setRejectDlg({ open: true, id: req.id, comment: '' })}>Reject</Button>
                      </>
                    )}
                    {/* Delete for SystemAdmin */}
                    {isSystemAdmin && (
                      <Tooltip title="Delete request">
                        <IconButton size="small" color="error" onClick={() => setDeleteDlg({ open: true, request: req })}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {/* Show dash if no actions available */}
                    {!isSystemAdmin && req.status !== 'PendingDM' && req.status !== 'PendingBoard' && <em>—</em>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={listFiltered.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[5, 10, 20, 50]}
        />
      </Paper>

      {/* Reject Dialog */}
      <Dialog open={rejectDlg.open} onClose={() => setRejectDlg({ open: false, id: null, comment: '' })}>
        <DialogTitle>Rejection Reason</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Comment"
            fullWidth
            variant="standard"
            value={rejectDlg.comment}
            onChange={(e) => setRejectDlg(d => ({ ...d, comment: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDlg({ open: false, id: null, comment: '' })}>Cancel</Button>
          <Button color="error" onClick={handleReject}>Reject</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDlg.open} onClose={() => setDeleteDlg({ open: false, request: null })}>
        <DialogTitle>Delete Confirmation</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this equipment request?</Typography>
          {deleteDlg.request && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>{deleteDlg.request.employeeName}</strong><br />
                {deleteDlg.request.subcategoryName} × {deleteDlg.request.quantity}<br />
                <Chip label={deleteDlg.request.status} size="small" sx={{ mt: 1 }} />
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDlg({ open: false, request: null })}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

